import { makeAutoObservable, observableShallow, runInAction } from 'mobx';
import { useEffect } from 'react';

import { campaignApi, type CampaignPack, type CampaignProgressPack } from '@shared/api';
import type { CategoryId } from '@shared/config/categories';
import { getViewerIdentity } from '@shared/lib/auth/viewer-identity';
import { getCampaignRewards, getEarnedCampaignRewards } from './campaign-rewards';
import { getCampaignAccess } from './campaign-access';

export type PublishedQuestion = {
  id: string;
  options: [string, string, string, string];
  optionTitleIds: [string, string, string, string];
  serverBacked: true;
  frameUrl: string;
  extraFrameUrl?: string;
  frameLabel: string;
  extraFrameLabel: string;
};
export type PublishedChapter = {
  id: string;
  campaignId: string;
  packId: string;
  earnedRewards?: { exp: number; coins: number };
  totalRewards?: { exp: number; coins: number };
  name: string;
  categoryId: CategoryId;
  difficulty: 1 | 2 | 3 | 4 | 5;
  kind: CampaignPack['kind'];
  questionIds: string[];
  questions: PublishedQuestion[];
};
const categoriesByKind = { film: 'movies', series: 'series', game: 'games', anime: 'anime' } as const;
export const publishedSessionKey = (campaignId: string, chapterId: string) =>
  `published-campaign:${campaignId}:${chapterId}`;

class PublishedCampaignStore {
  id = '';
  name = '';
  chapters: PublishedChapter[] = [];
  progressByChapter = new Map<string, CampaignProgressPack>();
  private loadedLogin: string | undefined;
  loaded = false;
  loading = false;
  error: string | null = null;
  private request: Promise<void> | null = null;
  progressError: string | null = null;
  private progressRequests = new Map<string, Promise<void>>();
  private progressUpdatedAt = new Map<string, number>();
  private progressRevision = new Map<string, number>();

  constructor() {
    makeAutoObservable<this, 'request' | 'progressRequests' | 'progressUpdatedAt' | 'progressRevision'>(
      this,
      {
        request: false,
        progressRequests: false,
        progressUpdatedAt: false,
        progressRevision: false,
        chapters: observableShallow,
      },
      { autoBind: true },
    );
    if (typeof window !== 'undefined') {
      window.addEventListener('uznali-session-changed', this.sessionChanged);
    }
  }

  private sessionChanged = () => {
    if (this.loaded && this.loadedLogin === getViewerIdentity()?.login) {
      void this.refresh(true);
      return;
    }
    this.loaded = false;
    this.progressByChapter.clear();
    this.chapters = this.chapters.map((chapter) => ({ ...chapter, earnedRewards: undefined }));
    void this.load(true);
  };

  refreshProgress(campaignId: string, login = getViewerIdentity()?.login, force = true): Promise<void> {
    if (!login || login !== this.loadedLogin) return Promise.resolve();
    const key = `${login}:${campaignId}`;
    const pending = this.progressRequests.get(key);
    if (pending) return pending;
    if (!force && Date.now() - (this.progressUpdatedAt.get(key) ?? 0) < 15_000) return Promise.resolve();
    const revision = this.progressRevision.get(key) ?? 0;
    const request = campaignApi
      .progress(campaignId)
      .then((state) => {
        if (
          getViewerIdentity()?.login !== login ||
          this.loadedLogin !== login ||
          (this.progressRevision.get(key) ?? 0) !== revision
        )
          return;
        runInAction(() => {
          state.packs.forEach((pack) => this.applyProgress(campaignId, pack, login));
          this.progressError = null;
        });
      })
      .catch(() => {
        if (getViewerIdentity()?.login === login && this.loadedLogin === login)
          runInAction(() => {
            this.progressError = 'Не удалось обновить прогресс кампании. Попробуйте ещё раз.';
          });
      })
      .finally(() => {
        if (this.progressRequests.get(key) === request) this.progressRequests.delete(key);
      });
    this.progressRequests.set(key, request);
    return request;
  }

  refresh = (force = false) =>
    Promise.all(
      [...new Set(this.chapters.map(({ campaignId }) => campaignId))].map((id) =>
        this.refreshProgress(id, undefined, force),
      ),
    );

  load = (force = false): Promise<void> => {
    const login = getViewerIdentity()?.login;
    if (this.request) return this.request;
    if (this.loaded && this.loadedLogin === login && !force) return Promise.resolve();
    if (this.loadedLogin !== login) {
      this.loaded = false;
      this.progressByChapter.clear();
      this.chapters = this.chapters.map((chapter) => ({ ...chapter, earnedRewards: undefined }));
    }
    this.loading = true;
    this.error = null;
    this.request = campaignApi
      .catalogue(Boolean(login))
      .then((catalogue) => {
        if (getViewerIdentity()?.login !== login) return;
        const counts = new Map<string, number>();
        catalogue.chapters.forEach((pack) => counts.set(pack.id, (counts.get(pack.id) ?? 0) + 1));
        const chapters: PublishedChapter[] = catalogue.chapters.map((pack) => ({
          id: counts.get(pack.id)! > 1 ? `${pack.campaignId}:${pack.id}` : pack.id,
          packId: pack.id,
          campaignId: pack.campaignId,
          name: pack.name,
          categoryId: categoriesByKind[pack.kind],
          difficulty: pack.difficulty as PublishedChapter['difficulty'],
          kind: pack.kind,
          questionIds: pack.questionIds,
          questions: [],
        }));
        runInAction(() => {
          this.id = catalogue.campaigns[0]?.id ?? '';
          this.name = catalogue.campaigns[0]?.name ?? '';
          this.loadedLogin = login;
          this.chapters = chapters;
          this.progressByChapter.clear();
          catalogue.states.forEach((state) =>
            state.packs.forEach((pack) => this.applyProgress(state.campaignId, pack, login)),
          );
          this.loaded = true;
          this.progressError = null;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.error = 'Не удалось загрузить кампанию. Попробуйте ещё раз.';
        });
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
          this.request = null;
          if (getViewerIdentity()?.login !== login) void this.load(true);
        });
      });
    return this.request;
  };

  progress(chapter: PublishedChapter, login?: string) {
    const state = login === this.loadedLogin ? this.progressByChapter.get(chapter.id) : undefined;
    return {
      answeredQuestions: state?.solvedCount ?? 0,
      complete: state?.status === 'completed' || (state !== undefined && state.solvedCount === state.questionCount),
      locked: state?.locked ?? true,
      rewards: getCampaignRewards([{ ...chapter, totalRewards: state?.totalRewards }]),
      earnedRewards: state ? { experience: state.earnedRewards.exp, coins: state.earnedRewards.coins } : undefined,
    };
  }

  applyProgress(campaignId: string, progress: CampaignProgressPack, login?: string) {
    if (login !== this.loadedLogin || getViewerIdentity()?.login !== login) return;
    const chapter = this.chapters.find((item) => item.campaignId === campaignId && item.packId === progress.packId);
    if (!chapter) throw new Error('Unknown campaign chapter');
    this.progressByChapter.set(chapter.id, progress);
    this.chapters = this.chapters.map((item) =>
      item === chapter ? { ...item, earnedRewards: progress.earnedRewards, totalRewards: progress.totalRewards } : item,
    );
    const key = `${login}:${campaignId}`;
    this.progressRevision.set(key, (this.progressRevision.get(key) ?? 0) + 1);
    this.progressUpdatedAt.set(key, Date.now());
    this.progressError = null;
  }

  access(selected: readonly CategoryId[], login?: string, categoryId?: string, chapterId?: string) {
    return getCampaignAccess(
      this.chapters.map((chapter) => ({ ...chapter, ...this.progress(chapter, login) })),
      categoryId,
      chapterId,
      selected,
    );
  }

  summary(selected: readonly CategoryId[], login?: string) {
    const chapters = this.chapters.filter((chapter) => selected.includes(chapter.categoryId));
    return {
      loading: !this.loaded,
      totalChapters: chapters.length,
      completedChapters: chapters.filter((chapter) => this.progress(chapter, login).complete).length,
      rewards: this.loaded && chapters.length > 0 ? getCampaignRewards(chapters) : undefined,
      earnedRewards: getEarnedCampaignRewards(chapters),
    };
  }
}

const publishedCampaign = new PublishedCampaignStore();
export const getPublishedCampaign = () => publishedCampaign;
export const usePublishedCampaign = () => {
  const login = getViewerIdentity()?.login;
  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (document.visibilityState === 'visible') void publishedCampaign.refresh();
    };
    void publishedCampaign.load().then(() => {
      if (active) refresh();
    });
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [login]);
  return publishedCampaign;
};
